import React from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../ui/Spinner';
import EmptyState from '../ui/EmptyState';
import { groupScheduledByDay, dayLabel, mstDayKey } from '../domainMetrics';

/* "Publishing next 7 days" — the scheduled-pages feed grouped by MST day.
   Each row links straight into the page editor so a scheduled post is one
   click from being reviewed. */

const timeOf = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Phoenix' });
};

const editHref = (page) =>
  `/domain/${encodeURIComponent(page.domain_id)}/pages/${encodeURIComponent(page.slug || '')}/edit`;

export default function ScheduledCard({ pages, days = 7, loading, failed }) {
  const groups = groupScheduledByDay(pages);
  const todayKey = mstDayKey(new Date());

  let body;
  if (loading) {
    body = <Spinner size={24} label="Loading schedule…" />;
  } else if (failed) {
    body = (
      <EmptyState
        title="Schedule unavailable"
        message="The scheduled-pages feed did not respond. Per-site counts in the table are still accurate."
      />
    );
  } else if (groups.length === 0) {
    body = (
      <EmptyState
        title="Nothing scheduled"
        message="Pick a site in the table below and write."
        action={<a className="btn btn-primary btn-sm" href="#sites">Go to sites</a>}
      />
    );
  } else {
    body = (
      <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
        {groups.map((group) => (
          <div key={group.day || 'undated'} style={{ marginBottom: '10px' }}>
            <div className="muted small" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 0' }}>
              {dayLabel(group.day, todayKey)}
            </div>
            {group.pages.map((page) => (
              <Link
                key={`${page.domain_id}/${page.slug}`}
                to={editHref(page)}
                title={`Edit ${page.title || page.slug} on ${page.hostname}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '64px minmax(90px, 1fr) minmax(0, 2fr)',
                  gap: '8px',
                  alignItems: 'baseline',
                  padding: '5px 6px',
                  borderRadius: '6px',
                  color: 'inherit',
                  textDecoration: 'none',
                  fontSize: '13px',
                }}
              >
                <span className="muted small" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {timeOf(page.published_at)}
                </span>
                <span className="muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {page.hostname}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {page.title || page.slug || '(untitled)'}
                </span>
              </Link>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="card" style={{ margin: 0 }}>
      <div className="card-header">
        <h2 className="card-title">Publishing next {days} days</h2>
        {!loading && !failed && pages.length > 0 && (
          <span className="muted small">{pages.length} page{pages.length === 1 ? '' : 's'}</span>
        )}
      </div>
      {body}
    </div>
  );
}
